import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-500.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/inter/latin-700.css";
import "@fontsource/plus-jakarta-sans/latin-700.css";
import "@fontsource/plus-jakarta-sans/latin-800.css";
// Warm-workshop redesign: Outfit 800/900 is the splash display face
// (poster headlines / particle-morph hero type). Dashboard faces above.

import { startPerformanceMonitoring } from "./lib/performance";
import { App } from "./App";
import { BootReveal } from "./boot/BootReveal";
import { BootRoot } from "./boot/BootRoot";
import { bootPlan } from "./boot/first-screen";
import { preloadFirstScreen } from "./boot/screens";
import { hasStoredSession } from "./boot/stored-session";
import { loadLocale } from "./i18n";
import { startErrorReporting } from "./lib/error-reporting";
import { initialLocale } from "./lib/locale-preference";
import { quickPrintPage } from "./quick-print-entry";
import "./styles.css";

// MERGED-OBS-002 / ADR-WES1: client-side Sentry, gated on a runtime DSN
// exactly as before (no DSN: no listeners, no requests). The SDK itself is a
// lazy chunk now (lib/sentry-client.ts holds the init options and the privacy
// rationale); errors before it loads are buffered in memory and replayed
// through the same scrubber. Started before the locale pre-load so an error
// during boot is still reported.
startErrorReporting({
  dsn: import.meta.env.VITE_SENTRY_DSN as string | undefined,
  environment: import.meta.env.VITE_SENTRY_ENV as string | undefined
});

// MERGED-PERF-001 / ADR-WES1: each locale's public copy is a small chunk.
// Pre-resolve the detected locale BEFORE the first render so the UI
// paints already-translated rather than flashing EN. The order mirrors
// App.tsx's shared locale initializer so the
// runtime locale and the pre-loaded chunk match. The head script
// (src/boot/head.ts) has already started downloading that chunk.
//
// ADR-WBC1: the first screen's module (splash, console stylesheet, docs) loads
// alongside, so the first render never shows a Suspense fallback. When #root
// already holds HTML (the prerendered splash or docs snapshot) it stays on
// screen until that render replaces it in one commit; only an empty #root
// gets the interim, locale-neutral boot frame.
const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);
if (!container.firstElementChild) {
  root.render(<BootReveal><div className="app-boot" role="status">Tinkr…</div></BootReveal>);
}
startPerformanceMonitoring();

const bootstrapLocale = initialLocale();
const renderApp = () => {
  root.render(
    <StrictMode>
      <BootRoot locale={bootstrapLocale}>
        <App />
      </BootRoot>
    </StrictMode>
  );
};

// EN public copy is bundled, so `loadLocale("en")` is a no-op fast path.
const localeReady = loadLocale(bootstrapLocale).catch((error: unknown) => {
  // If the chunk fails to load (network blip, deployment race), still
  // render — translate() falls back to EN per-key so the app is usable
  // in English until the user retries or picks a different locale.
  console.error("Failed to load locale chunk", {
    locale: bootstrapLocale,
    error
  });
});
// A Quick Print page (when Quick Print is open) renders its own screen, not
// the splash the URL would otherwise fall back to, so it waits for nothing.
const firstScreenReady = quickPrintPage(window.location.pathname)
  ? Promise.resolve()
  : preloadFirstScreen(bootPlan(window.location, hasStoredSession(import.meta.env)));
void Promise.all([localeReady, firstScreenReady]).then(renderApp);
